/* eslint-disable no-unused-vars */
export enum UnauthorizedErrorCodes {
  ExpiredToken = 'EXPIRED_TOKEN',
  InvalidCaptchaToken = 'INVALID_CAPTCHA_TOKEN',
  AdditionalThirdPartiesUnauthorized = 'ADDITIONAL_THIRD_PARTIES_UNAUTHORIZED',
}

export enum HttpStatus {
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  GONE = 410,
  TOO_MANY_REQUESTS = 429,
}

export enum NotFoundErrorCode {
  SubmissionNotFound = 'SUBMISSION_NOT_FOUND',
  SettingsNotFound = 'SETTINGS_NOT_FOUND',
  FileNotFound = 'FILE_NOT_FOUND',
  ThirdPartyNotFound = 'THIRD_PARTY_NOT_FOUND',
  AdditionalThirdPartiesNotFound = 'ADDITIONAL_THIRD_PARTIES_NOT_FOUND',
}

export enum GoneErrorCode {
  TradeReferenceNoLongerRequired = 'TRADE_REFERENCE_NO_LONGER_REQUIRED',
  PersonalGuarantorNoLongerRequired = 'PERSONAL_GUARANTOR_NO_LONGER_REQUIRED',
  TradeReferenceCodeExpired = 'TRADE_REFERENCE_CODE_EXPIRED',
  TradeReferenceCodeExhausted = 'TRADE_REFERENCE_CODE_EXHAUSTED',
  TradeReferenceAlreadySubmitted = 'TRADE_REFERENCE_ALREADY_SUBMITTED',
  PersonalGuarantorCodeExpired = 'PERSONAL_GUARANTOR_CODE_EXPIRED',
  PersonalGuarantorCodeExhausted = 'PERSONAL_GUARANTOR_CODE_EXHAUSTED',
  PersonalGuarantorAlreadySubmitted = 'PERSONAL_GUARANTOR_ALREADY_SUBMITTED',
}

export type ErrorCode = NotFoundErrorCode | UnauthorizedErrorCodes | GoneErrorCode;

export interface ApiError {
  status: number;
  message: string;
  code?: ErrorCode;
}

export const isBadRequestError = (err: unknown): err is ApiError =>
  isApiError(err) && err.status === HttpStatus.BAD_REQUEST;

export const isNotFoundError = (err: unknown): err is ApiError =>
  isApiError(err) && err.status === HttpStatus.NOT_FOUND;

export const isUnauthorizedError = (err: unknown): err is ApiError =>
  isApiError(err) && err.status === HttpStatus.UNAUTHORIZED;

export const isForbiddenError = (err: unknown): err is ApiError =>
  isApiError(err) && err.status === HttpStatus.FORBIDDEN;

export const isGoneError = (err: unknown): err is ApiError =>
  isApiError(err) && err.status === HttpStatus.GONE;

export const isTooManyRequestsError = (err: unknown): err is ApiError =>
  isApiError(err) && err.status === HttpStatus.TOO_MANY_REQUESTS;

export const isExpiredToken = (err: unknown): err is ApiError =>
  isUnauthorizedError(err) && err.code === UnauthorizedErrorCodes.ExpiredToken;

export const isAdditionalThirdPartiesUnauthorized = (err: unknown): err is ApiError =>
  isUnauthorizedError(err) && err.code === UnauthorizedErrorCodes.AdditionalThirdPartiesUnauthorized;

export const isAdditionalThirdPartiesNotFound = (err: unknown): err is ApiError =>
  isNotFoundError(err) && err.code === NotFoundErrorCode.AdditionalThirdPartiesNotFound;

export const isApiError = (error: unknown): error is ApiError => {
  return typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'message' in error &&
    typeof (error as any).status === 'number' &&
    typeof (error as any).message === 'string';
};

export const apiError = (status: number, message: string, code?: ErrorCode): ApiError => {
  const error = new Error(message) as ApiError & Error;
  error.status = status;
  error.code = code;
  return error;
};
